import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useNewBillsAPI from "../../api/billsAPI";
import PageTabs from "../../Components/Common/PageTabs";
import {
  restBillsData,
  settingBillsData,
} from "../../../features/billsPageSlice";
import BillTransactionHistory from "./components/BillTransactionHistory";
import ViewToggle from "./components/ViewToggle";
import BillProviderCard from "./components/BillProviderCard";
import AutomatedBills from "./components/AutomatedBills";
import BillsPageSkeleton from "./components/BillsPageSkeleton";
import ApiErrorState from "../../Components/Common/ApiErrorState";
import TableFilters from "../../Components/Common/TableFilters";
import "../../Styles/pages/bills.scss";

function filterProviders(providers, searchQuery) {
  const normalizedQuery = String(searchQuery || "").trim().toLowerCase();

  if (!normalizedQuery) return providers;

  return providers.filter((provider) => {
    const searchableValues = [
      provider?.Title,
      provider?.Description,
      provider?.MainButtonLabel,
      ...(Array.isArray(provider?.Buttons)
        ? provider.Buttons.map((button) => button?.Label)
        : []),
    ];

    return searchableValues.some((value) =>
      String(value || "").toLowerCase().includes(normalizedQuery),
    );
  });
}

function BillsPageHeading() {
  return (
    <div className="bills-page__heading">
      <div className="bills-page__heading-icon" aria-hidden="true">
        <img src="/Images/bills-icon.svg" alt="" />
      </div>
      <div className="bills-page__heading-copy">
        <h1 className="mb-0">Bills</h1>
        <p>Pay and manage your utility bills.</p>
      </div>
    </div>
  );
}

export default function BillsPage({ onOpenQrAfterGenerate }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { GetAllBills } = useNewBillsAPI();
  const isShowTransactionHistory = useSelector(
    (state) => state?.billsPage?.isShowTransactionHistory,
  );

  const [providers, setProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [activeTab, setActiveTab] = useState("payments");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const tabs = useMemo(
    () => [
      { id: "payments", label: "Bill Payments" },
      { id: "automated", label: "Automated Bills" },
      { id: "history", label: "Bill Payment History" },
    ],
    [],
  );

  useEffect(() => {
    if (isShowTransactionHistory) {
      setActiveTab("history");
    }
  }, [isShowTransactionHistory]);

  useEffect(() => {
    if (isShowTransactionHistory) {
      return undefined;
    }

    let isMounted = true;

    const loadBills = async () => {
      setLoading(true);
      setError("");

      try {
        const payload = await GetAllBills();

        if (!isMounted) return;

        setProviders(Array.isArray(payload?.data) ? payload.data : []);

        if (payload?.UsersToReceiveNotification) {
          sessionStorage.setItem(
            "UsersToReceiveNotification",
            payload.UsersToReceiveNotification,
          );
        }
      } catch (requestError) {
        if (!isMounted) return;
        setError("Unable to load bill providers right now. Please try again.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBills();

    return () => {
      isMounted = false;
    };
  }, [isShowTransactionHistory, reloadKey]);

  const filteredProviders = filterProviders(providers, searchQuery);
  const historyProviders = useMemo(
    () => filteredProviders.filter((provider) => provider?.MainButtonLabel),
    [filteredProviders],
  );

  const handleProviderAction = useCallback((slug) => {
    if (!slug) return;

    dispatch(restBillsData());
    const providerSlug = String(slug).split("?")[0];
    const selectedProvider = providers.find(
      (provider) => String(provider?.Title || "").trim().toLowerCase() === providerSlug,
    );
    navigate(slug, {
      state: selectedProvider ? { provider: selectedProvider } : undefined,
    });
  }, [dispatch, navigate, providers]);

  const handleTransactionHistory = useCallback((provider) => {
    dispatch(settingBillsData({ field: "UtilityID", value: provider?.UtilityID }));
    dispatch(settingBillsData({ field: "pageTitle", value: provider?.Title || "Bills" }));
    dispatch(settingBillsData({ field: "isShowTransactionHistory", value: true }));
    dispatch(settingBillsData({ field: "TransactionImage", value: provider?.Image || "" }));
    dispatch(
      settingBillsData({
        field: "TransactionDescription",
        value: provider?.Description || "",
      }),
    );
    setActiveTab("history");
  }, [dispatch]);

  const handleTabChange = useCallback(
    (tabId) => {
      if (tabId !== "history" && isShowTransactionHistory) {
        dispatch(restBillsData());
      }
      setActiveTab(tabId);
    },
    [dispatch, isShowTransactionHistory],
  );

  const renderProvidersGrid = useCallback(
    ({
      emptyDescription,
      emptyTitle,
      historyButtonLabel,
      historyOnly = false,
      resultsLabel,
      sectionTitle,
    }) => (
      <section className="bills-page__providers">
        <TableFilters
          className="bills-page__toolbar"
          searchPlaceholder="Search bills..."
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          customActions={<ViewToggle value={viewMode} onChange={setViewMode} />}
        />

        <div className="bills-page__section-header">
          <h2>{sectionTitle}</h2>
          <span className="bills-page__results">{resultsLabel}</span>
        </div>

        {error ? (
          <ApiErrorState
            message={error}
            onRetry={() => setReloadKey((value) => value + 1)}
          />
        ) : filteredProviders.length > 0 && (!historyOnly || historyProviders.length > 0) ? (
          <div className={`row g-3 bills-page__providers-grid is-${viewMode}`}>
            {(historyOnly ? historyProviders : filteredProviders).map((provider, index) => (
              <div
                key={provider?.UtilityID || provider?.Title || index}
                className={
                  viewMode === "grid"
                    ? "col-12 col-md-6 col-xl-4"
                    : "col-12"
                }
              >
                <BillProviderCard
                  historyButtonLabel={historyButtonLabel}
                  historyOnly={historyOnly}
                  provider={provider}
                  viewMode={viewMode}
                  onAction={handleProviderAction}
                  onHistory={handleTransactionHistory}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="bills-page__empty-panel">
            <h3>{emptyTitle}</h3>
            <p>{emptyDescription}</p>
          </div>
        )}
      </section>
    ),
    [
      error,
      filteredProviders,
      handleProviderAction,
      handleTransactionHistory,
      historyProviders,
      searchQuery,
      viewMode,
    ],
  );

  if (isShowTransactionHistory) {
    return (
      <div className="bills-page">
        <BillsPageHeading />

        <PageTabs
          activeTab={activeTab}
          ariaLabel="Bills sections"
          className="bills-page__tabs"
          onChange={handleTabChange}
          tabs={tabs}
        />

        <BillTransactionHistory onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
      </div>
    );
  }

  return (
    <div className="bills-page">
      <BillsPageHeading />

      <PageTabs
        activeTab={activeTab}
        ariaLabel="Bills sections"
        className="bills-page__tabs"
        onChange={handleTabChange}
        tabs={tabs}
      />

      {activeTab === "automated" ? (
        <AutomatedBills providers={providers} />
      ) : loading ? (
        <BillsPageSkeleton
          resultsLabel={
            activeTab === "history" ? "Loading history providers..." : "Loading providers..."
          }
          sectionTitle={
            activeTab === "history" ? "Bill Payment History" : "All Bill Providers"
          }
          viewMode={viewMode}
        />
      ) : activeTab === "history" ? (
        renderProvidersGrid({
          emptyDescription:
            "Bill providers with payment history will appear here when available.",
          emptyTitle: "No bill payment history providers",
          historyButtonLabel: "View History",
          historyOnly: true,
          resultsLabel: `${historyProviders.length} provider${historyProviders.length === 1 ? "" : "s"}`,
          sectionTitle: "Bill Payment History",
        })
      ) : (
        renderProvidersGrid({
          emptyDescription: "Try a different search term or clear the current filter.",
          emptyTitle: "No providers found",
          historyOnly: false,
          resultsLabel: `${filteredProviders.length} provider${filteredProviders.length === 1 ? "" : "s"}`,
          sectionTitle: "All Bill Providers",
        })
      )}
    </div>
  );
}
